import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
} from '@mui/material';
import { formatDistanceToNowStrict } from 'date-fns';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArchiveIcon from '@mui/icons-material/Archive';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { globalAdminAPI } from '../../services/api';
import CaseTransferManager from './CaseTransferManager';
import UniversityAiConfigurationPanel, {
  AI_FUNDING_MODES,
  normalizeUniversityAiFundingMode,
} from './UniversityAiConfigurationPanel';
import UniversityAiWorkloadSettings from './UniversityAiWorkloadSettings';
import BillingTab from './global-admin/BillingTab';
import ComplianceTab from './global-admin/ComplianceTab';
import FeatureChecklistTab from './global-admin/FeatureChecklistTab';
import MarkingTab from './global-admin/MarkingTab';
import OverviewTab from './global-admin/OverviewTab';
import RuntimeAssetsTab from './global-admin/RuntimeAssetsTab';
import { MetricCard, RiskChip } from './global-admin/AdminUi';
import {
  TAB_KEYS,
  GEMINI_REASONING_EFFORT_OPTIONS,
  UNITY_ADDRESSABLE_PLATFORMS,
  defaultAddressablesForm,
  defaultAddressablesOverview,
  defaultBillingForm,
  defaultCreateFormState,
  defaultMarkingForm,
  defaultPilotForm,
  defaultRealtimeForm,
  defaultTranscriptionForm,
  defaultUniversityAiForm,
  defaultVoiceSampleForm,
  defaultVoiceSamplesOverview,
} from './global-admin/config';
import {
  buildContextWindowCompressionPayload,
  buildGeminiThinkingPayload,
  computeUniversityErrors,
  formatInviteExpiry,
  formatRelativeTime,
  formatTokenBalance,
  isAzureMarkingConfigured,
  isAzureTranscriptionConfigured,
  isEditableElement,
  isValidAdminEmail,
  mapContextWindowCompressionToForm,
  mapFeedbackSeverity,
  normalizeGeminiReasoningEffort,
  resolveGeminiReasoningEffort,
} from './global-admin/utils';

const GlobalAdminConsole = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [billingForm, setBillingForm] = useState(defaultBillingForm);
  const [pilotForm, setPilotForm] = useState(defaultPilotForm);
  const [realtimeForm, setRealtimeForm] = useState(defaultRealtimeForm);
  const [markingForm, setMarkingForm] = useState(defaultMarkingForm);
  const [transcriptionForm, setTranscriptionForm] = useState(defaultTranscriptionForm);
  const [featureChecklist, setFeatureChecklist] = useState([]);
  const [tenantSnapshot, setTenantSnapshot] = useState(null);
  const [tenantFilters, setTenantFilters] = useState({ status: 'all', search: '' });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [financeOverview, setFinanceOverview] = useState(null);
  const [loadingFinance, setLoadingFinance] = useState(true);
  const [exportingFinance, setExportingFinance] = useState(false);
  const [creditInvoices, setCreditInvoices] = useState([]);
  const [creditInvoicesLoading, setCreditInvoicesLoading] = useState(false);
  const [creditInvoiceAlert, setCreditInvoiceAlert] = useState(null);
  const [creditInvoiceFilters, setCreditInvoiceFilters] = useState({ status: 'all' });
  const [creditInvoiceActions, setCreditInvoiceActions] = useState({ sending: null, markingPaid: null, downloading: null });
  const [creditInvoiceCreateForm, setCreditInvoiceCreateForm] = useState({ universityId: '', quantity: '' });
  const [creatingCreditInvoice, setCreatingCreditInvoice] = useState(false);
  const [selectedUniversityId, setSelectedUniversityId] = useState('');
  const [universityDetail, setUniversityDetail] = useState(null);
  const [universityForm, setUniversityForm] = useState(null);
  const [assignAdminForm, setAssignAdminForm] = useState({ email: '', firstName: '', lastName: '' });
  const [assigningAdmin, setAssigningAdmin] = useState(false);
  const [assignAdminFeedback, setAssignAdminFeedback] = useState(null);
  const [inviteActionState, setInviteActionState] = useState({ resendId: null, revokeId: null });
  const [loadingUniversityDetail, setLoadingUniversityDetail] = useState(false);
  const [universityAiConfig, setUniversityAiConfig] = useState(null);
  const [universityAiForm, setUniversityAiForm] = useState(defaultUniversityAiForm);
  const [universityAiLoading, setUniversityAiLoading] = useState(false);
  const [universityAiSaving, setUniversityAiSaving] = useState(false);
  const [universityAiAlert, setUniversityAiAlert] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creatingUniversity, setCreatingUniversity] = useState(false);
  const [savingUniversity, setSavingUniversity] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [savingPilot, setSavingPilot] = useState(false);
  const [savingRealtime, setSavingRealtime] = useState(false);
  const [savingMarking, setSavingMarking] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [createForm, setCreateForm] = useState(defaultCreateFormState);
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [universityFormErrors, setUniversityFormErrors] = useState({});
  const [addressablesForm, setAddressablesForm] = useState(defaultAddressablesForm);
  const [addressablesOverview, setAddressablesOverview] = useState(defaultAddressablesOverview);
  const [addressablesUploadState, setAddressablesUploadState] = useState({});
  const [addressablesBuildActionState, setAddressablesBuildActionState] = useState({});
  const [addressablesEditState, setAddressablesEditState] = useState({});
  const [voiceSamplesOverview, setVoiceSamplesOverview] = useState(defaultVoiceSamplesOverview);
  const [voiceSampleForm, setVoiceSampleForm] = useState(defaultVoiceSampleForm);
  const [voiceSampleActionState, setVoiceSampleActionState] = useState({
    uploading: false,
    deleting: false,
    error: '',
  });

  const showToast = useCallback((message, severity = 'info') => {
    if (!message) {
      return;
    }
    setToast({ open: true, message, severity });
  }, []);

  const handleToastClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    setError('');
    try {
      const [overviewResponse, featureResponse, voiceSamplesResponse, addressablesResponse] = await Promise.all([
        globalAdminAPI.getSystemSettings(),
        globalAdminAPI.getFeatureChecklist(),
        globalAdminAPI.getVoiceSamples(),
        globalAdminAPI.getUnityAddressables(),
      ]);
      const overview = overviewResponse.data || overviewResponse;
      const voiceSamplesData = voiceSamplesResponse.data || voiceSamplesResponse || defaultVoiceSamplesOverview;
      const addressablesData = addressablesResponse.data || addressablesResponse || defaultAddressablesOverview;
      setSettings(overview);
      setAddressablesOverview({
        builds: Array.isArray(addressablesData?.builds) ? addressablesData.builds : [],
        config: {
          bucket: addressablesData?.config?.bucket || '',
          prefix: addressablesData?.config?.prefix || '',
          publicBaseUrl: addressablesData?.config?.publicBaseUrl || '',
          currentBaseUrls: UNITY_ADDRESSABLE_PLATFORMS.reduce((acc, platform) => {
            acc[platform.key] = addressablesData?.config?.currentBaseUrls?.[platform.key] || '';
            return acc;
          }, {}),
        },
      });
      setAddressablesEditState(() => {
        const next = {};
        (Array.isArray(addressablesData?.builds) ? addressablesData.builds : []).forEach((build) => {
          next[build._id] = {
            version: build.version ?? '',
            buildNumber: build.buildNumber ?? '',
          };
        });
        return next;
      });
      setVoiceSamplesOverview({
        providers: Array.isArray(voiceSamplesData.providers) ? voiceSamplesData.providers : defaultVoiceSamplesOverview.providers,
        voices: {
          openai: Array.isArray(voiceSamplesData?.voices?.openai) ? voiceSamplesData.voices.openai : [],
          gemini: Array.isArray(voiceSamplesData?.voices?.gemini) ? voiceSamplesData.voices.gemini : [],
        },
        samples: {
          openai: Array.isArray(voiceSamplesData?.samples?.openai) ? voiceSamplesData.samples.openai : [],
          gemini: Array.isArray(voiceSamplesData?.samples?.gemini) ? voiceSamplesData.samples.gemini : [],
        },
        counts: {
          openai: Number(voiceSamplesData?.counts?.openai) || 0,
          gemini: Number(voiceSamplesData?.counts?.gemini) || 0,
          total: Number(voiceSamplesData?.counts?.total) || 0,
        },
      });
      setVoiceSampleForm((prev) => {
        const provider = (prev.provider === 'gemini' || prev.provider === 'openai') ? prev.provider : 'openai';
        const providerVoices = Array.isArray(voiceSamplesData?.voices?.[provider]) ? voiceSamplesData.voices[provider] : [];
        const selectedVoice = providerVoices.includes(prev.voice) ? prev.voice : (providerVoices[0] || '');
        return {
          ...prev,
          provider,
          voice: selectedVoice,
          file: null,
        };
      });
      setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
      setBillingForm({
        freeSignupTokens: overview.billing?.freeSignupTokens ?? 15,
        historyCostTokens: overview.billing?.historyCostTokens ?? 1,
        userRegistrationCap: overview.billing?.userRegistrationCap ?? 0,
        receiptsEnabled: overview.billing?.receiptsEnabled ?? true,
        vatInvoicesEnabled: overview.billing?.vatInvoicesEnabled ?? true,
        refundPolicy: overview.billing?.refundPolicy || '',
        refundWindowDays: overview.billing?.refundWindowDays ?? 14,
        tokenPacks: (overview.billing?.tokenPacks || []).map((pack) => ({
          key: pack.key,
          name: pack.name,
          tokens: pack.tokens,
          price: ((pack.priceCents || 0) / 100).toFixed(2),
          currency: (pack.currency || 'GBP').toUpperCase(),
          historiesEquivalent: pack.historiesEquivalent ?? 0,
          stripePriceId: pack.stripePriceId || '',
          appleProductId: pack.appleProductId || '',
          googleProductId: pack.googleProductId || '',
          isActive: pack.isActive !== false,
        })),
      });
      setPilotForm({
        enabled: typeof overview.pilot?.enabled === 'boolean'
          ? overview.pilot.enabled
          : true,
      });
      const realtimeSettings = overview.openai?.realtime || {};
      const markingSettings = overview.openai?.marking || {};
      const transcriptionSettings = overview.openai?.transcription || {};
      const mapParam = (value, fallback = '') => {
        if (value === null || value === undefined || value === '') {
          return fallback;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? String(numeric) : String(value);
      };
      setRealtimeForm({
        ...defaultRealtimeForm,
        provider: realtimeSettings.provider || 'openai',
        parentApiKey: '',
        realtimeModel: realtimeSettings.realtimeModel || overview.openai?.allowedRealtimeModels?.[0] || '',
        voice: realtimeSettings.voice || overview.openai?.voices?.[0] || '',
        proactiveAudio: Boolean(realtimeSettings.proactiveAudio),
        affectiveDialog: Boolean(realtimeSettings.affectiveDialog),
        reasoningEffort: resolveGeminiReasoningEffort(realtimeSettings),
        ...mapContextWindowCompressionToForm(realtimeSettings.contextWindowCompression),
        azureEndpoint: realtimeSettings.azure?.endpoint || '',
        azureApiKey: '',
      });
      setMarkingForm({
        ...defaultMarkingForm,
        provider: markingSettings.provider || realtimeSettings.provider || 'openai',
        parentApiKey: '',
        markingModel: markingSettings.markingModel || overview.openai?.allowedMarkingModels?.[0] || '',
        azureEndpoint: markingSettings.azure?.endpoint || '',
        azureDeploymentId: markingSettings.azure?.deploymentId || '',
        azureApiVersion: markingSettings.azure?.apiVersion || '',
        azureResourceName: markingSettings.azure?.resourceName || '',
        azureApiKey: '',
        azureUseActiveDirectory: Boolean(markingSettings.azure?.useActiveDirectory),
        parameters: {
          temperature: mapParam(markingSettings.parameters?.temperature, defaultMarkingForm.parameters.temperature),
          maxOutputTokens: mapParam(markingSettings.parameters?.maxOutputTokens),
          topP: mapParam(markingSettings.parameters?.topP),
          frequencyPenalty: mapParam(markingSettings.parameters?.frequencyPenalty),
          presencePenalty: mapParam(markingSettings.parameters?.presencePenalty),
          reasoningEffort: markingSettings.parameters?.reasoningEffort || '',
          verbosity: markingSettings.parameters?.verbosity || defaultMarkingForm.parameters.verbosity,
        },
      });
      setTranscriptionForm({
        ...defaultTranscriptionForm,
        enabled: Boolean(transcriptionSettings.enabled),
        provider: transcriptionSettings.provider || 'openai',
        parentApiKey: '',
        model: transcriptionSettings.model || defaultTranscriptionForm.model,
        azureEndpoint: transcriptionSettings.azure?.endpoint || '',
        azureDeploymentId: transcriptionSettings.azure?.deploymentId || '',
        azureApiVersion: transcriptionSettings.azure?.apiVersion || '',
        azureResourceName: transcriptionSettings.azure?.resourceName || '',
        azureApiKey: '',
        azureUseActiveDirectory: Boolean(transcriptionSettings.azure?.useActiveDirectory),
      });
      setFeatureChecklist(featureResponse.data || featureResponse || []);
    } catch (err) {
      console.error('Failed to load global settings', err);
      setError(err?.response?.data?.message || 'Unable to load global settings');
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    setLoadingTenants(true);
    setError('');
    try {
      const response = await globalAdminAPI.listTenants();
      setTenantSnapshot(response.data || response);
    } catch (err) {
      console.error('Failed to load tenant snapshot', err);
      setError(err?.response?.data?.message || 'Unable to load tenant snapshot');
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  const loadFinance = useCallback(async () => {
    setLoadingFinance(true);
    try {
      const response = await globalAdminAPI.getFinanceOverview();
      setFinanceOverview(response.data || response);
    } catch (err) {
      console.error('Failed to load finance overview', err);
      setError((prev) => prev || err?.response?.data?.message || 'Unable to load finance overview');
    } finally {
      setLoadingFinance(false);
    }
  }, []);

  const loadCreditInvoices = useCallback(async () => {
    setCreditInvoicesLoading(true);
    setCreditInvoiceAlert(null);
    try {
      const params = {};
      if (creditInvoiceFilters.status && creditInvoiceFilters.status !== 'all') {
        params.status = creditInvoiceFilters.status;
      }
      const response = await globalAdminAPI.listCreditInvoices(params);
      setCreditInvoices(response.data?.invoices || response.invoices || []);
    } catch (err) {
      console.error('Failed to load credit invoices', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to load credit invoices',
      });
    } finally {
      setCreditInvoicesLoading(false);
    }
  }, [creditInvoiceFilters.status]);

  const handleCreditInvoiceFilterChange = (field) => (event) => {
    setCreditInvoiceFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleAddressablesFieldChange = (platform, field) => (event) => {
    const value = field === 'file' ? event.target.files?.[0] || null : event.target.value;
    setAddressablesForm((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
    if (field === 'file') {
      event.target.value = null;
    }
    setAddressablesUploadState((prev) => {
      if (!prev[platform]?.error) {
        return prev;
      }
      return { ...prev, [platform]: { ...prev[platform], error: '' } };
    });
  };

  const handleUploadAddressables = async (platform) => {
    const selection = addressablesForm[platform] || {};
    if (!selection.version?.trim()) {
      setAddressablesUploadState((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], uploading: false, error: 'Enter a version before uploading.' },
      }));
      return;
    }
    if (!selection.file) {
      setAddressablesUploadState((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], uploading: false, error: 'Select a .zip file to upload.' },
      }));
      return;
    }
    setAddressablesUploadState((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], uploading: true, error: '' },
    }));
    try {
      await globalAdminAPI.uploadUnityAddressables({
        platform,
        version: selection.version?.trim(),
        buildNumber: selection.buildNumber?.trim(),
        notes: selection.notes?.trim(),
        file: selection.file,
      });
      setAddressablesForm((prev) => ({
        ...prev,
        [platform]: { ...defaultAddressablesForm[platform] },
      }));
      setAddressablesUploadState((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], uploading: false, error: '' },
      }));
      const platformLabel = UNITY_ADDRESSABLE_PLATFORMS.find((item) => item.key === platform)?.label || platform;
      showToast(`${platformLabel} addressables uploaded`, 'success');
      await loadSettings();
    } catch (err) {
      setAddressablesUploadState((prev) => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          uploading: false,
          error: err?.response?.data?.message || 'Unable to upload addressables build.',
        },
      }));
    }
  };

  const setAddressablesBuildState = useCallback((buildId, patch = {}) => {
    setAddressablesBuildActionState((prev) => ({
      ...prev,
      [buildId]: { ...prev[buildId], ...patch },
    }));
  }, []);

  const handleAddressablesBuildEditFieldChange = (buildId, field) => (event) => {
    const value = event.target.value;
    setAddressablesEditState((prev) => ({
      ...prev,
      [buildId]: {
        ...(prev[buildId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveAddressablesBuild = async (build) => {
    const editState = addressablesEditState[build._id] || {};
    if (!editState.version?.trim()) {
      showToast('Version is required', 'error');
      return;
    }

    setAddressablesBuildState(build._id, { saving: true, error: '' });
    try {
      await globalAdminAPI.updateUnityAddressablesBuild(build._id, {
        version: editState.version.trim(),
        buildNumber: editState.buildNumber?.trim() || '',
      });
      showToast(`${build.platform} addressables build updated`, 'success');
      await loadSettings();
    } catch (err) {
      setAddressablesBuildState(build._id, {
        saving: false,
        error: err?.response?.data?.message || 'Unable to update addressables build.',
      });
      return;
    }
    setAddressablesBuildState(build._id, { saving: false, error: '' });
  };

  const handleActivateAddressablesBuild = async (build) => {
    setAddressablesBuildState(build._id, { activating: true, error: '' });
    try {
      await globalAdminAPI.activateUnityAddressablesBuild(build._id);
      showToast(`${build.platform} addressables build is now active`, 'success');
      await loadSettings();
    } catch (err) {
      setAddressablesBuildState(build._id, {
        activating: false,
        error: err?.response?.data?.message || 'Unable to activate addressables build.',
      });
      return;
    }
    setAddressablesBuildState(build._id, { activating: false, error: '' });
  };

  const handleDeleteAddressablesBuild = async (build) => {
    if (!window.confirm(`Delete ${build.platform} addressables build ${build.version}${build.buildNumber ? ` (${build.buildNumber})` : ''}?`)) {
      return;
    }

    setAddressablesBuildState(build._id, { deleting: true, error: '' });
    try {
      await globalAdminAPI.deleteUnityAddressablesBuild(build._id);
      showToast(`${build.platform} addressables build deleted`, 'success');
      await loadSettings();
    } catch (err) {
      setAddressablesBuildState(build._id, {
        deleting: false,
        error: err?.response?.data?.message || 'Unable to delete addressables build.',
      });
      return;
    }
    setAddressablesBuildState(build._id, { deleting: false, error: '' });
  };

  const assignAdminEmailValid = useMemo(
    () => isValidAdminEmail(assignAdminForm.email),
    [assignAdminForm.email],
  );
  const addressablesByPlatform = useMemo(() => {
    const entries = settings?.unityAddressables || [];
    return entries.reduce((acc, entry) => {
      if (entry?.platform) {
        acc[entry.platform] = entry;
      }
      return acc;
    }, {});
  }, [settings?.unityAddressables]);
  const addressableBuildsByPlatform = useMemo(() => {
    const builds = Array.isArray(addressablesOverview?.builds) ? addressablesOverview.builds : [];
    return UNITY_ADDRESSABLE_PLATFORMS.reduce((acc, platform) => {
      acc[platform.key] = builds.filter((entry) => entry?.platform === platform.key);
      return acc;
    }, {});
  }, [addressablesOverview]);

  const voiceOptionsForProvider = useMemo(() => {
    const provider = voiceSampleForm.provider === 'gemini' ? 'gemini' : 'openai';
    const source = voiceSamplesOverview?.voices?.[provider];
    return Array.isArray(source) ? source : [];
  }, [voiceSamplesOverview, voiceSampleForm.provider]);

  const selectedVoiceSample = useMemo(() => {
    const provider = voiceSampleForm.provider === 'gemini' ? 'gemini' : 'openai';
    const source = voiceSamplesOverview?.samples?.[provider];
    const entries = Array.isArray(source) ? source : [];
    return entries.find((entry) => entry.voice === voiceSampleForm.voice) || null;
  }, [voiceSamplesOverview, voiceSampleForm.provider, voiceSampleForm.voice]);

  const handleVoiceSampleProviderChange = (event) => {
    const provider = event.target.value === 'gemini' ? 'gemini' : 'openai';
    const options = Array.isArray(voiceSamplesOverview?.voices?.[provider])
      ? voiceSamplesOverview.voices[provider]
      : [];
    setVoiceSampleForm((prev) => ({
      ...prev,
      provider,
      voice: options.includes(prev.voice) ? prev.voice : (options[0] || ''),
      file: null,
    }));
    setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
  };

  const handleVoiceSampleVoiceChange = (event) => {
    setVoiceSampleForm((prev) => ({ ...prev, voice: event.target.value, file: null }));
    setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
  };

  const handleVoiceSampleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setVoiceSampleForm((prev) => ({ ...prev, file }));
    event.target.value = null;
    setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
  };

  const handleUploadVoiceSample = async () => {
    if (!voiceSampleForm.provider || !voiceSampleForm.voice) {
      setVoiceSampleActionState((prev) => ({ ...prev, error: 'Choose a provider and voice.' }));
      return;
    }
    if (!voiceSampleForm.file) {
      setVoiceSampleActionState((prev) => ({ ...prev, error: 'Select an .mp3 or .wav file to upload.' }));
      return;
    }

    setVoiceSampleActionState((prev) => ({ ...prev, uploading: true, error: '' }));
    try {
      await globalAdminAPI.uploadVoiceSample({
        provider: voiceSampleForm.provider,
        voice: voiceSampleForm.voice,
        file: voiceSampleForm.file,
      });
      setVoiceSampleForm((prev) => ({ ...prev, file: null }));
      setVoiceSampleActionState((prev) => ({ ...prev, uploading: false, error: '' }));
      showToast(`Uploaded sample for ${voiceSampleForm.provider} / ${voiceSampleForm.voice}`, 'success');
      await loadSettings();
    } catch (err) {
      setVoiceSampleActionState((prev) => ({
        ...prev,
        uploading: false,
        error: err?.response?.data?.message || 'Unable to upload voice sample.',
      }));
    }
  };

  const handleDeleteVoiceSample = async () => {
    if (!voiceSampleForm.provider || !voiceSampleForm.voice) {
      setVoiceSampleActionState((prev) => ({ ...prev, error: 'Choose a provider and voice.' }));
      return;
    }

    setVoiceSampleActionState((prev) => ({ ...prev, deleting: true, error: '' }));
    try {
      await globalAdminAPI.deleteVoiceSample(voiceSampleForm.provider, voiceSampleForm.voice);
      setVoiceSampleActionState((prev) => ({ ...prev, deleting: false, error: '' }));
      showToast(`Removed sample for ${voiceSampleForm.provider} / ${voiceSampleForm.voice}`, 'success');
      await loadSettings();
    } catch (err) {
      setVoiceSampleActionState((prev) => ({
        ...prev,
        deleting: false,
        error: err?.response?.data?.message || 'Unable to remove voice sample.',
      }));
    }
  };

  const loadUniversityDetail = useCallback(async (universityId) => {
    if (!universityId) {
      setUniversityDetail(null);
      setUniversityForm(null);
      setUniversityAiConfig(null);
      setUniversityAiForm(defaultUniversityAiForm);
      setUniversityAiAlert(null);
      setAssignAdminFeedback(null);
      return null;
    }

    setLoadingUniversityDetail(true);
    setError('');

    try {
      const response = await globalAdminAPI.getUniversity(universityId);
      const detail = response.data || response;
      setUniversityDetail(detail);
      setUniversityForm({
        name: detail.name || '',
        code: detail.code || '',
        slug: detail.slug || '',
        status: detail.status || 'active',
        seatCapacity: detail.seatCapacity ?? 0,
        contactEmail: detail.contact?.email || '',
        contactPhone: detail.contact?.phone || '',
        creditUnitPrice: Number.isFinite(Number(detail.creditPricing?.unitPriceCents))
          ? (Number(detail.creditPricing?.unitPriceCents) / 100).toFixed(2)
          : '',
        creditCurrency: (detail.creditPricing?.currency || 'GBP').toUpperCase(),
        creditAllocationMode: detail.creditAllocationMode || 'assigned',
      });
      setAssignAdminForm({ email: '', firstName: '', lastName: '' });
      setInviteActionState({ resendId: null, revokeId: null });
      setUniversityFormErrors({});
      return detail;
    } catch (err) {
      console.error('Failed to load university detail', err);
      const message = err?.response?.data?.message || 'Unable to load university details';
      setError(message);
      setUniversityDetail(null);
      setUniversityForm(null);
      setAssignAdminFeedback({ type: 'error', message });
      return null;
    } finally {
      setLoadingUniversityDetail(false);
    }
  }, []);

  const loadUniversityAiConfig = useCallback(async (universityId) => {
    if (!universityId) {
      setUniversityAiConfig(null);
      setUniversityAiForm(defaultUniversityAiForm);
      return;
    }
    setUniversityAiLoading(true);
    setUniversityAiAlert(null);
    try {
      const response = await globalAdminAPI.getUniversityAiConfig(universityId);
      const data = response.data || response;
      setUniversityAiConfig(data);
      const mapParam = (value, fallback = '') => {
        if (value === null || value === undefined || value === '') {
          return fallback;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? String(numeric) : String(value);
      };
      const realtime = data.realtime || {};
      const marking = data.marking || {};
      const transcription = data.transcription || {};
      const fundingMode = normalizeUniversityAiFundingMode(data.funding?.mode);
      const useGlobalCredentials = fundingMode === AI_FUNDING_MODES.PLATFORM_WALLET;
      setUniversityAiForm({
        funding: { mode: fundingMode },
        realtime: {
          ...defaultUniversityAiForm.realtime,
          provider: realtime.provider || 'openai',
          parentApiKey: '',
          realtimeModel: realtime.realtimeModel || data.allowedRealtimeModels?.[0] || '',
          voice: realtime.voice || (data.voices || [])[0] || '',
          proactiveAudio: Boolean(realtime.proactiveAudio),
          affectiveDialog: Boolean(realtime.affectiveDialog),
          reasoningEffort: resolveGeminiReasoningEffort(realtime),
          ...mapContextWindowCompressionToForm(realtime.contextWindowCompression),
          azureEndpoint: realtime.azure?.endpoint || '',
          azureApiKey: '',
          useGlobalCredentials,
        },
        marking: {
          ...defaultUniversityAiForm.marking,
          provider: marking.provider || realtime.provider || 'openai',
          parentApiKey: '',
          markingModel: marking.markingModel || data.allowedMarkingModels?.[0] || '',
          azureEndpoint: marking.azure?.endpoint || '',
          azureDeploymentId: marking.azure?.deploymentId || '',
          azureApiVersion: marking.azure?.apiVersion || '',
          azureResourceName: marking.azure?.resourceName || '',
          azureApiKey: '',
          azureUseActiveDirectory: Boolean(marking.azure?.useActiveDirectory),
          useGlobalCredentials,
          parameters: {
            temperature: mapParam(marking.parameters?.temperature, defaultMarkingForm.parameters.temperature),
            maxOutputTokens: mapParam(marking.parameters?.maxOutputTokens),
            topP: mapParam(marking.parameters?.topP),
            frequencyPenalty: mapParam(marking.parameters?.frequencyPenalty),
            presencePenalty: mapParam(marking.parameters?.presencePenalty),
            reasoningEffort: marking.parameters?.reasoningEffort || '',
            verbosity: marking.parameters?.verbosity || defaultMarkingForm.parameters.verbosity,
          },
        },
        transcription: {
          ...defaultUniversityAiForm.transcription,
          enabled: Boolean(transcription.enabled),
          provider: transcription.provider || 'openai',
          parentApiKey: '',
          model: transcription.model || defaultTranscriptionForm.model,
          azureEndpoint: transcription.azure?.endpoint || '',
          azureDeploymentId: transcription.azure?.deploymentId || '',
          azureApiVersion: transcription.azure?.apiVersion || '',
          azureResourceName: transcription.azure?.resourceName || '',
          azureApiKey: '',
          azureUseActiveDirectory: Boolean(transcription.azure?.useActiveDirectory),
          useGlobalCredentials,
        },
      });
    } catch (err) {
      console.error('Failed to load university AI config', err);
      setUniversityAiAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to load AI configuration',
      });
    } finally {
      setUniversityAiLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadTenants();
    loadFinance();
  }, [loadSettings, loadTenants, loadFinance]);

  useEffect(() => {
    if (TAB_KEYS[activeTab] === 'billing') {
      loadCreditInvoices();
    }
  }, [activeTab, loadCreditInvoices]);

  const handleTabKeyDown = useCallback((event) => {
    if (event.defaultPrevented) {
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    if (isEditableElement(event.target)) {
      return;
    }
    event.preventDefault();
    setActiveTab((prev) => {
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = prev + delta;
      if (next < 0) {
        return 0;
      }
      if (next >= TAB_KEYS.length) {
        return TAB_KEYS.length - 1;
      }
      return next;
    });
  }, []);

  const handleBillingFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setBillingForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePilotFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setPilotForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePackFieldChange = (index, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setBillingForm((prev) => {
      const nextPacks = prev.tokenPacks.map((pack, idx) => (idx === index ? { ...pack, [field]: value } : pack));
      return { ...prev, tokenPacks: nextPacks };
    });
  };

  const handleAssignAdminFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAssignAdminForm((prev) => ({ ...prev, [field]: value }));
    if (assignAdminFeedback?.type === 'error') {
      setAssignAdminFeedback(null);
    }
  };

  const handleSendCreditInvoice = async (invoiceId) => {
    if (!invoiceId) {
      return;
    }
    setCreditInvoiceActions((prev) => ({ ...prev, sending: invoiceId }));
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.sendCreditInvoice(invoiceId);
      const updated = response.data || response;
      setCreditInvoices((prev) => {
        const shouldInclude = creditInvoiceFilters.status === 'all' || creditInvoiceFilters.status === updated.status;
        if (!shouldInclude) {
          return prev.filter((invoice) => invoice.id !== invoiceId);
        }
        return prev.map((invoice) => (invoice.id === invoiceId ? { ...invoice, ...updated } : invoice));
      });
      setCreditInvoiceAlert({ severity: 'success', message: `Invoice ${updated.invoiceNumber} sent successfully.` });
    } catch (err) {
      console.error('Failed to send credit invoice', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to send credit invoice',
      });
    } finally {
      setCreditInvoiceActions((prev) => ({ ...prev, sending: null }));
    }
  };

  const handleMarkCreditInvoicePaid = async (invoiceId) => {
    if (!invoiceId) {
      return;
    }
    setCreditInvoiceActions((prev) => ({ ...prev, markingPaid: invoiceId }));
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.markCreditInvoicePaid(invoiceId);
      const updated = response.data || response;
      setCreditInvoices((prev) => {
        const shouldInclude = creditInvoiceFilters.status === 'all' || creditInvoiceFilters.status === updated.status;
        if (!shouldInclude) {
          return prev.filter((invoice) => invoice.id !== invoiceId);
        }
        return prev.map((invoice) => (invoice.id === invoiceId ? { ...invoice, ...updated } : invoice));
      });
      setCreditInvoiceAlert({ severity: 'success', message: `Invoice ${updated.invoiceNumber} marked as paid.` });
    } catch (err) {
      console.error('Failed to mark credit invoice paid', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to mark credit invoice paid',
      });
    } finally {
      setCreditInvoiceActions((prev) => ({ ...prev, markingPaid: null }));
    }
  };

  const handleDownloadCreditInvoicePdf = async (invoice) => {
    if (!invoice?.id) {
      return;
    }
    setCreditInvoiceActions((prev) => ({ ...prev, downloading: invoice.id }));
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.downloadCreditInvoicePdf(invoice.id);
      const data = response.data || response;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download credit invoice PDF', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to download credit invoice PDF',
      });
    } finally {
      setCreditInvoiceActions((prev) => ({ ...prev, downloading: null }));
    }
  };

  const handleCreateCreditInvoice = async () => {
    const quantity = Number.parseInt(creditInvoiceCreateForm.quantity, 10);
    if (!creditInvoiceCreateForm.universityId) {
      setCreditInvoiceAlert({ severity: 'warning', message: 'Select a university before creating an invoice.' });
      return;
    }
    if (!Number.isInteger(quantity) || quantity <= 0) {
      setCreditInvoiceAlert({ severity: 'warning', message: 'Enter a valid credit quantity.' });
      return;
    }

    setCreatingCreditInvoice(true);
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.createCreditInvoice({
        universityId: creditInvoiceCreateForm.universityId,
        quantity,
      });
      const created = response.data || response;
      await loadCreditInvoices();
      setCreditInvoiceCreateForm((prev) => ({ ...prev, quantity: '' }));
      setCreditInvoiceAlert({ severity: 'success', message: `Draft invoice ${created.invoiceNumber} created successfully.` });
    } catch (err) {
      console.error('Failed to create credit invoice', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to create credit invoice',
      });
    } finally {
      setCreatingCreditInvoice(false);
    }
  };

  const submitAssignAdmin = async () => {
    if (!selectedUniversityId) {
      setAssignAdminFeedback({
        type: 'error',
        message: 'Select a university before assigning administrators.',
      });
      return;
    }

    if (!assignAdminEmailValid) {
      setAssignAdminFeedback({
        type: 'error',
        message: 'Enter a valid administrator email address.',
      });
      return;
    }

    const email = assignAdminForm.email.trim().toLowerCase();

    setAssigningAdmin(true);
    setAssignAdminFeedback(null);
    try {
      await globalAdminAPI.assignUniversityAdmin(selectedUniversityId, {
        email,
        firstName: assignAdminForm.firstName.trim(),
        lastName: assignAdminForm.lastName.trim(),
      });
      setAssignAdminFeedback({
        type: 'success',
        message: 'Administrator invite sent successfully.',
      });
      setAssignAdminForm({ email: '', firstName: '', lastName: '' });
      await loadUniversityDetail(selectedUniversityId);
    } catch (err) {
      console.error('Failed to assign university admin', err);
      const message = err?.response?.data?.message || 'Failed to assign university admin';
      setAssignAdminFeedback({ type: 'error', message });
      setError(message);
    } finally {
      setAssigningAdmin(false);
    }
  };

  const handleResendAdminInvite = async (inviteId) => {
    if (!inviteId) {
      return;
    }

    setInviteActionState((prev) => ({ ...prev, resendId: inviteId }));
    try {
      await globalAdminAPI.resendInvite(inviteId);
      setAssignAdminFeedback({ type: 'success', message: 'Invite resent successfully.' });
      await loadUniversityDetail(selectedUniversityId);
    } catch (err) {
      console.error('Failed to resend invite', err);
      const message = err?.response?.data?.message || 'Unable to resend invite';
      setAssignAdminFeedback({ type: 'error', message });
      setError(message);
    } finally {
      setInviteActionState((prev) => ({ ...prev, resendId: null }));
    }
  };

  const handleRevokeAdminInvite = async (inviteId) => {
    if (!inviteId) {
      return;
    }

    setInviteActionState((prev) => ({ ...prev, revokeId: inviteId }));
    try {
      await globalAdminAPI.revokeInvite(inviteId);
      setAssignAdminFeedback({ type: 'info', message: 'Invite revoked.' });
      await loadUniversityDetail(selectedUniversityId);
    } catch (err) {
      console.error('Failed to revoke invite', err);
      const message = err?.response?.data?.message || 'Unable to revoke invite';
      setAssignAdminFeedback({ type: 'error', message });
      setError(message);
    } finally {
      setInviteActionState((prev) => ({ ...prev, revokeId: null }));
    }
  };

  const handleCreateFieldChange = (field) => (event) => {
    const value = event.target.value;
    setCreateForm((prev) => ({ ...prev, [field]: value }));
    setCreateFormErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleUniversityFieldChange = (field) => (event) => {
    const value = event.target.value;
    setUniversityForm((prev) => ({ ...(prev || {}), [field]: value }));
    setUniversityFormErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const addTokenPack = () => {
    setBillingForm((prev) => ({
      ...prev,
      tokenPacks: [
        ...prev.tokenPacks,
        {
          key: `custom-${Date.now()}`,
          name: 'New Pack',
          tokens: 0,
          price: '0.00',
          currency: 'GBP',
          historiesEquivalent: 0,
          stripePriceId: '',
          appleProductId: '',
          googleProductId: '',
          isActive: true,
        },
      ],
    }));
  };

  const removeTokenPack = (index) => {
    setBillingForm((prev) => ({
      ...prev,
      tokenPacks: prev.tokenPacks.filter((_, idx) => idx !== index),
    }));
  };

  const openCreateUniversityDialog = () => {
    setCreateForm({ ...defaultCreateFormState });
    setCreateFormErrors({});
    setCreateDialogOpen(true);
  };

  const closeCreateUniversityDialog = () => {
    setCreateDialogOpen(false);
    setCreateForm({ ...defaultCreateFormState });
    setCreateFormErrors({});
  };

  const submitCreateUniversity = async () => {
    const validationErrors = computeUniversityErrors(createForm);
    if (Object.keys(validationErrors).length) {
      setCreateFormErrors(validationErrors);
      setError('Please correct the highlighted fields.');
      return;
    }

    setCreatingUniversity(true);
    setError('');
    try {
      const payload = {
        name: createForm.name,
        code: createForm.code,
        slug: createForm.slug,
        seatCapacity: Number.parseInt(createForm.seatCapacity, 10) || 0,
        status: createForm.status,
        contact: {
          email: createForm.contactEmail,
          phone: createForm.contactPhone,
        },
      };
      const response = await globalAdminAPI.createUniversity(payload);
      const detail = response.data || response;
      setSuccessMessage(`Created ${detail.name}`);
      setError('');
      setCreateDialogOpen(false);
      setCreateForm({ ...defaultCreateFormState });
      setCreateFormErrors({});
      await loadTenants();
      setSelectedUniversityId(detail.id);
      await loadUniversityDetail(detail.id);
    } catch (err) {
      console.error('Failed to create university', err);
      setError(err?.response?.data?.message || 'Unable to create university');
    } finally {
      setCreatingUniversity(false);
    }
  };

  const saveUniversityChanges = async () => {
    if (!selectedUniversityId || !universityForm) {
      return;
    }
    const validationErrors = computeUniversityErrors(universityForm);
    if (Object.keys(validationErrors).length) {
      setUniversityFormErrors(validationErrors);
      setError('Please correct the highlighted fields.');
      return;
    }
    setSavingUniversity(true);
    setError('');
    try {
      const creditUnitPrice = Number.parseFloat(universityForm.creditUnitPrice);
      const creditUnitPriceCents = Number.isFinite(creditUnitPrice) ? Math.round(creditUnitPrice * 100) : 0;
      const payload = {
        name: universityForm.name,
        code: universityForm.code,
        slug: universityForm.slug,
        status: universityForm.status,
        seatCapacity: Number.parseInt(universityForm.seatCapacity, 10) || 0,
        contact: {
          email: universityForm.contactEmail,
          phone: universityForm.contactPhone,
        },
        creditUnitPriceCents,
        creditCurrency: (universityForm.creditCurrency || 'GBP').toUpperCase(),
        creditAllocationMode: universityForm.creditAllocationMode || 'assigned',
      };
      const response = await globalAdminAPI.updateUniversity(selectedUniversityId, payload);
      const detail = response.data || response;
      setUniversityDetail(detail);
      setUniversityForm({
        name: detail.name || '',
        code: detail.code || '',
        slug: detail.slug || '',
        status: detail.status || 'active',
        seatCapacity: detail.seatCapacity ?? 0,
        contactEmail: detail.contact?.email || '',
        contactPhone: detail.contact?.phone || '',
        creditUnitPrice: Number.isFinite(Number(detail.creditPricing?.unitPriceCents))
          ? (Number(detail.creditPricing?.unitPriceCents) / 100).toFixed(2)
          : '',
        creditCurrency: (detail.creditPricing?.currency || 'GBP').toUpperCase(),
        creditAllocationMode: detail.creditAllocationMode || 'assigned',
      });
      setUniversityFormErrors({});
      setSuccessMessage(`Updated ${detail.name}`);
      setError('');
      await loadTenants();
    } catch (err) {
      console.error('Failed to update university', err);
      setError(err?.response?.data?.message || 'Unable to update university');
    } finally {
      setSavingUniversity(false);
    }
  };

  const archiveSelectedUniversity = async () => {
    if (!selectedUniversityId) {
      return;
    }
    const reason = window.prompt('Provide a reason for archiving this university (optional):', '');
    if (reason === null) {
      return;
    }
    setSavingUniversity(true);
    setError('');
    try {
      const response = await globalAdminAPI.archiveUniversity(selectedUniversityId, { reason });
      const detail = response.data || response;
      setUniversityDetail(detail);
      setUniversityForm({
        name: detail.name || '',
        code: detail.code || '',
        slug: detail.slug || '',
        status: detail.status || 'archived',
        seatCapacity: detail.seatCapacity ?? 0,
        contactEmail: detail.contact?.email || '',
        contactPhone: detail.contact?.phone || '',
        creditUnitPrice: Number.isFinite(Number(detail.creditPricing?.unitPriceCents))
          ? (Number(detail.creditPricing?.unitPriceCents) / 100).toFixed(2)
          : '',
        creditCurrency: (detail.creditPricing?.currency || 'GBP').toUpperCase(),
        creditAllocationMode: detail.creditAllocationMode || 'assigned',
      });
      setUniversityFormErrors({});
      setSuccessMessage('');
      setError('');
      showToast(`Archived ${detail.name}`, 'success');
      await loadTenants();
    } catch (err) {
      console.error('Failed to archive university', err);
      setError(err?.response?.data?.message || 'Unable to archive university');
      showToast(err?.response?.data?.message || 'Unable to archive university', 'error');
    } finally {
      setSavingUniversity(false);
    }
  };

  const handleSelectUniversity = async (universityId) => {
    setSelectedUniversityId(universityId);
    setUniversityFormErrors({});
    setAssignAdminFeedback(null);
    await loadUniversityDetail(universityId);
    await loadUniversityAiConfig(universityId);
  };

  const handleExportFinanceReport = async () => {
    setExportingFinance(true);
    try {
      const response = await globalAdminAPI.exportFinanceReport();
      const data = response.data || response;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'finance-report.csv';
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export finance report', err);
      setError(err?.response?.data?.message || 'Unable to export finance report');
    } finally {
      setExportingFinance(false);
    }
  };

  const updateUniversityAiFundingMode = (mode) => {
    const normalizedMode = normalizeUniversityAiFundingMode(mode);
    const useGlobalCredentials = normalizedMode === AI_FUNDING_MODES.PLATFORM_WALLET;
    setUniversityAiForm((prev) => ({
      ...prev,
      funding: { mode: normalizedMode },
      realtime: {
        ...(prev.realtime || defaultUniversityAiForm.realtime),
        useGlobalCredentials,
      },
      marking: {
        ...(prev.marking || defaultUniversityAiForm.marking),
        useGlobalCredentials,
      },
      transcription: {
        ...(prev.transcription || defaultUniversityAiForm.transcription),
        useGlobalCredentials,
      },
    }));
    setUniversityAiAlert(null);
  };

  const updateUniversityRealtimeField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      realtime: {
        ...(prev.realtime || defaultUniversityAiForm.realtime),
        [field]: value,
        ...(field === 'provider' ? { parentApiKey: '' } : {}),
      },
    }));
  };

  const updateUniversityMarkingField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      marking: {
        ...(prev.marking || defaultUniversityAiForm.marking),
        [field]: value,
      },
    }));
  };

  const updateUniversityTranscriptionField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      transcription: {
        ...(prev.transcription || defaultUniversityAiForm.transcription),
        [field]: value,
      },
    }));
  };

  const updateMarkingParameterField = (field) => (event) => {
    const value = event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      marking: {
        ...(prev.marking || defaultUniversityAiForm.marking),
        parameters: {
          ...(prev.marking?.parameters || defaultMarkingForm.parameters),
          [field]: value,
        },
      },
    }));
  };

  const saveUniversityAiConfig = async () => {
    if (!selectedUniversityId) {
      return;
    }
    const normalizeNumber = (value) => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    };
    setUniversityAiSaving(true);
    setUniversityAiAlert(null);
    try {
      const realtime = universityAiForm.realtime || defaultUniversityAiForm.realtime;
      const marking = universityAiForm.marking || defaultUniversityAiForm.marking;
      const transcription = universityAiForm.transcription || defaultUniversityAiForm.transcription;
      const fundingMode = normalizeUniversityAiFundingMode(universityAiForm.funding?.mode);
      const isTenantByok = fundingMode === AI_FUNDING_MODES.TENANT_BYOK;
      const useGlobalCredentials = !isTenantByok;
      const realtimeModel = realtime.realtimeModel?.trim();
      const markingModel = marking.markingModel?.trim();
      const transcriptionModel = transcription.model?.trim();
      if (isTenantByok && (!realtimeModel || !markingModel)) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Realtime and marking model names are required.',
        });
        return;
      }
      if (isTenantByok && realtime.provider === 'azure' && !realtime.azureEndpoint?.trim()) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Provide the Azure OpenAI resource endpoint for Realtime WebRTC.',
        });
        return;
      }
      const markingEndpointConfigured = isAzureMarkingConfigured({
        endpoint: marking.azureEndpoint,
        deploymentId: marking.azureDeploymentId,
        apiVersion: marking.azureApiVersion,
      });
      if (isTenantByok && marking.provider === 'azure' && !markingEndpointConfigured) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Provide either a full Azure responses endpoint URL (with api-version) or endpoint + deployment id + API version for marking.',
        });
        return;
      }
      const tenantAlreadyUsesByok = universityAiConfig?.funding?.mode === AI_FUNDING_MODES.TENANT_BYOK;
      const existingRealtimeKeyConfigured = tenantAlreadyUsesByok && (
        realtime.provider === 'gemini'
          ? universityAiConfig?.realtime?.geminiKeyConfigured
          : (realtime.provider === 'azure'
            ? universityAiConfig?.realtime?.azureKeyConfigured
            : universityAiConfig?.realtime?.openaiKeyConfigured)
      );
      if (
        isTenantByok
        && realtime.provider !== 'azure'
        && !realtime.parentApiKey?.trim()
        && !existingRealtimeKeyConfigured
      ) {
        setUniversityAiAlert({
          severity: 'warning',
          message: `Add a ${realtime.provider === 'gemini' ? 'Gemini' : 'OpenAI'} realtime API key before enabling university-managed AI.`,
        });
        return;
      }
      if (
        isTenantByok
        && realtime.provider === 'azure'
        && !realtime.azureApiKey?.trim()
        && !existingRealtimeKeyConfigured
      ) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Add an Azure OpenAI API key before enabling university-managed Realtime WebRTC.',
        });
        return;
      }
      if (isTenantByok && transcription.enabled && !transcriptionModel) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Transcription model name is required when transcription override is enabled.',
        });
        return;
      }
      const transcriptionEndpointConfigured = isAzureTranscriptionConfigured({
        endpoint: transcription.azureEndpoint,
        deploymentId: transcription.azureDeploymentId,
        apiVersion: transcription.azureApiVersion,
      });
      if (
        transcription.enabled
        && isTenantByok
        && transcription.provider === 'azure'
        && !transcriptionEndpointConfigured
      ) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Provide either a full Azure audio transcriptions endpoint URL (with api-version) or endpoint + deployment id + API version for transcription.',
        });
        return;
      }
      await globalAdminAPI.updateUniversityAiConfig(selectedUniversityId, {
        funding: { mode: fundingMode },
        realtime: {
          provider: realtime.provider || 'openai',
          parentApiKey: realtime.parentApiKey,
          realtimeModel,
          voice: realtime.voice,
          proactiveAudio: Boolean(realtime.proactiveAudio),
          affectiveDialog: Boolean(realtime.affectiveDialog),
          ...buildGeminiThinkingPayload(realtime),
          contextWindowCompression: buildContextWindowCompressionPayload(realtime),
          azureEndpoint: realtime.azureEndpoint,
          azureApiKey: realtime.azureApiKey,
          useGlobalCredentials,
        },
        marking: {
          provider: marking.provider || realtime.provider || 'openai',
          parentApiKey: marking.parentApiKey,
          markingModel,
          azureEndpoint: marking.azureEndpoint,
          azureDeploymentId: marking.azureDeploymentId,
          azureApiVersion: marking.azureApiVersion,
          azureResourceName: marking.azureResourceName,
          azureApiKey: marking.azureApiKey,
          azureUseActiveDirectory: marking.azureUseActiveDirectory,
          useGlobalCredentials,
          parameters: {
            temperature: normalizeNumber(marking.parameters?.temperature),
            maxOutputTokens: normalizeNumber(marking.parameters?.maxOutputTokens),
            topP: normalizeNumber(marking.parameters?.topP),
            frequencyPenalty: normalizeNumber(marking.parameters?.frequencyPenalty),
            presencePenalty: normalizeNumber(marking.parameters?.presencePenalty),
            reasoningEffort: marking.parameters?.reasoningEffort?.trim() || undefined,
            verbosity: marking.parameters?.verbosity?.trim() || defaultMarkingForm.parameters.verbosity,
          },
        },
        transcription: {
          enabled: Boolean(transcription.enabled),
          provider: transcription.provider || 'openai',
          parentApiKey: transcription.parentApiKey,
          model: transcriptionModel || defaultTranscriptionForm.model,
          azureEndpoint: transcription.azureEndpoint,
          azureDeploymentId: transcription.azureDeploymentId,
          azureApiVersion: transcription.azureApiVersion,
          azureResourceName: transcription.azureResourceName,
          azureApiKey: transcription.azureApiKey,
          azureUseActiveDirectory: transcription.azureUseActiveDirectory,
          useGlobalCredentials,
        },
      });
      setUniversityAiAlert({ severity: 'success', message: 'AI configuration updated successfully' });
      setUniversityAiForm((prev) => ({
        funding: { mode: fundingMode },
        realtime: { ...(prev.realtime || {}), parentApiKey: '', azureApiKey: '' },
        marking: { ...(prev.marking || {}), parentApiKey: '', azureApiKey: '' },
        transcription: { ...(prev.transcription || {}), parentApiKey: '', azureApiKey: '' },
      }));
      await loadUniversityAiConfig(selectedUniversityId);
    } catch (err) {
      setUniversityAiAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to update AI configuration',
      });
    } finally {
      setUniversityAiSaving(false);
    }
  };

  const savePilotSettings = async () => {
    setSavingPilot(true);
    setSuccessMessage('');
    setError('');
    try {
      await globalAdminAPI.updatePilotSettings({
        enabled: Boolean(pilotForm.enabled),
      });
      setSuccessMessage('Pilot mode updated successfully');
      await loadSettings();
    } catch (err) {
      console.error('Failed to update pilot settings', err);
      setError(err?.response?.data?.message || 'Unable to update pilot mode');
    } finally {
      setSavingPilot(false);
    }
  };

  const saveBillingSettings = async () => {
    setSavingBilling(true);
    setSuccessMessage('');
    setError('');
    try {
      const payload = {
        freeSignupTokens: Number.parseInt(billingForm.freeSignupTokens, 10) || 0,
        historyCostTokens: Number.parseInt(billingForm.historyCostTokens, 10) || 0,
        userRegistrationCap: Number.parseInt(billingForm.userRegistrationCap, 10) || 0,
        receiptsEnabled: Boolean(billingForm.receiptsEnabled),
        vatInvoicesEnabled: Boolean(billingForm.vatInvoicesEnabled),
        refundPolicy: billingForm.refundPolicy,
        refundWindowDays: Number.parseInt(billingForm.refundWindowDays, 10) || 0,
        tokenPacks: billingForm.tokenPacks.map((pack) => ({
          key: pack.key,
          name: pack.name,
          tokens: Number.parseInt(pack.tokens, 10) || 0,
          priceCents: Math.round((Number.parseFloat(pack.price) || 0) * 100),
          currency: (pack.currency || 'GBP').toLowerCase(),
          historiesEquivalent: Number.parseInt(pack.historiesEquivalent, 10) || 0,
          stripePriceId: pack.stripePriceId || '',
          appleProductId: pack.appleProductId || '',
          googleProductId: pack.googleProductId || '',
          isActive: Boolean(pack.isActive),
        })),
      };
      await globalAdminAPI.updateBillingSettings(payload);
      setSuccessMessage('Billing settings updated successfully');
      await loadSettings();
    } catch (err) {
      console.error('Failed to update billing settings', err);
      setError(err?.response?.data?.message || 'Unable to update billing settings');
    } finally {
      setSavingBilling(false);
    }
  };

  const saveOpenAiSettings = async (target = 'realtime') => {
    if (target === 'marking') {
      setSavingMarking(true);
    } else {
      setSavingRealtime(true);
    }
    setSuccessMessage('');
    setError('');
    const normalizeNumber = (value) => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    };
      try {
        const realtimeModel = realtimeForm.realtimeModel?.trim();
        const markingModel = markingForm.markingModel?.trim();
        const transcriptionModel = transcriptionForm.model?.trim();
        if (!realtimeModel) {
          setError('Realtime model name is required.');
          return;
        }
      if (!markingModel) {
        setError('Marking model name is required.');
        return;
      }
      const existingRealtimeKeyConfigured = realtimeForm.provider === 'gemini'
        ? settings?.openai?.realtime?.geminiKeyConfigured
        : (realtimeForm.provider === 'azure'
          ? settings?.openai?.realtime?.azureKeyConfigured
          : settings?.openai?.realtime?.openaiKeyConfigured);
      if (
        realtimeForm.provider !== 'azure'
        && !realtimeForm.parentApiKey?.trim()
        && !existingRealtimeKeyConfigured
      ) {
        setError(`Add a ${realtimeForm.provider === 'gemini' ? 'Gemini' : 'OpenAI'} realtime API key before saving.`);
        return;
      }
      if (realtimeForm.provider === 'azure' && !realtimeForm.azureEndpoint?.trim()) {
        setError('Provide the Azure OpenAI resource endpoint for Realtime WebRTC.');
        return;
      }
      if (
        realtimeForm.provider === 'azure'
        && !realtimeForm.azureApiKey?.trim()
        && !existingRealtimeKeyConfigured
      ) {
        setError('Add an Azure OpenAI API key before saving Realtime WebRTC settings.');
        return;
      }
      const markingEndpointConfigured = isAzureMarkingConfigured({
        endpoint: markingForm.azureEndpoint,
        deploymentId: markingForm.azureDeploymentId,
        apiVersion: markingForm.azureApiVersion,
      });
      if (markingForm.provider === 'azure' && !markingEndpointConfigured) {
        setError('Provide either a full Azure responses endpoint URL (with api-version) or endpoint + deployment id + API version for marking.');
        return;
      }
      if (transcriptionForm.enabled && !transcriptionModel) {
        setError('Transcription model name is required when override is enabled.');
        return;
      }
      const transcriptionEndpointConfigured = isAzureTranscriptionConfigured({
        endpoint: transcriptionForm.azureEndpoint,
        deploymentId: transcriptionForm.azureDeploymentId,
        apiVersion: transcriptionForm.azureApiVersion,
      });
      if (transcriptionForm.enabled && transcriptionForm.provider === 'azure' && !transcriptionEndpointConfigured) {
        setError('Provide either a full Azure audio transcriptions endpoint URL (with api-version) or endpoint + deployment id + API version for transcription.');
        return;
      }

        await globalAdminAPI.updateOpenAiSettings({
          realtime: {
            provider: realtimeForm.provider || 'openai',
            parentApiKey: realtimeForm.parentApiKey,
            realtimeModel,
            voice: realtimeForm.voice,
            proactiveAudio: Boolean(realtimeForm.proactiveAudio),
            affectiveDialog: Boolean(realtimeForm.affectiveDialog),
            ...buildGeminiThinkingPayload(realtimeForm),
            contextWindowCompression: buildContextWindowCompressionPayload(realtimeForm),
            azureEndpoint: realtimeForm.azureEndpoint,
          azureApiKey: realtimeForm.azureApiKey,
        },
        marking: {
          provider: markingForm.provider || realtimeForm.provider || 'openai',
          parentApiKey: markingForm.parentApiKey,
          markingModel,
          azureEndpoint: markingForm.azureEndpoint,
          azureDeploymentId: markingForm.azureDeploymentId,
          azureApiVersion: markingForm.azureApiVersion,
          azureResourceName: markingForm.azureResourceName,
          azureApiKey: markingForm.azureApiKey,
          azureUseActiveDirectory: markingForm.azureUseActiveDirectory,
          parameters: {
            temperature: normalizeNumber(markingForm.parameters?.temperature),
            maxOutputTokens: normalizeNumber(markingForm.parameters?.maxOutputTokens),
            topP: normalizeNumber(markingForm.parameters?.topP),
            frequencyPenalty: normalizeNumber(markingForm.parameters?.frequencyPenalty),
            presencePenalty: normalizeNumber(markingForm.parameters?.presencePenalty),
            reasoningEffort: markingForm.parameters?.reasoningEffort?.trim() || undefined,
            verbosity: markingForm.parameters?.verbosity?.trim() || 'medium',
          },
        },
        transcription: {
          enabled: Boolean(transcriptionForm.enabled),
          provider: transcriptionForm.provider || 'openai',
          parentApiKey: transcriptionForm.parentApiKey,
          model: transcriptionModel || defaultTranscriptionForm.model,
          azureEndpoint: transcriptionForm.azureEndpoint,
          azureDeploymentId: transcriptionForm.azureDeploymentId,
          azureApiVersion: transcriptionForm.azureApiVersion,
          azureResourceName: transcriptionForm.azureResourceName,
          azureApiKey: transcriptionForm.azureApiKey,
          azureUseActiveDirectory: transcriptionForm.azureUseActiveDirectory,
        },
      });
      setSuccessMessage('OpenAI defaults updated successfully');
      setRealtimeForm((prev) => ({ ...prev, parentApiKey: '', azureApiKey: '' }));
      setMarkingForm((prev) => ({ ...prev, parentApiKey: '', azureApiKey: '' }));
      setTranscriptionForm((prev) => ({ ...prev, parentApiKey: '', azureApiKey: '' }));
      await loadSettings();
    } catch (err) {
      console.error('Failed to update OpenAI settings', err);
      setError(err?.response?.data?.message || 'Unable to update OpenAI settings');
    } finally {
      setSavingRealtime(false);
      setSavingMarking(false);
    }
  };

  const realtimeTenantForm = universityAiForm.realtime || defaultUniversityAiForm.realtime;
  const markingTenantForm = universityAiForm.marking || defaultUniversityAiForm.marking;
  const transcriptionTenantForm = universityAiForm.transcription || defaultUniversityAiForm.transcription;

  const filteredTenants = useMemo(() => {
    if (!tenantSnapshot?.tenants) {
      return [];
    }
    return tenantSnapshot.tenants.filter((tenant) => {
      const matchesStatus = tenantFilters.status === 'all' || tenant.status === tenantFilters.status;
      const matchesSearch = tenantFilters.search
        ? tenant.name.toLowerCase().includes(tenantFilters.search.toLowerCase())
          || tenant.code?.toLowerCase()?.includes(tenantFilters.search.toLowerCase())
        : true;
    return matchesStatus && matchesSearch;
  });
  }, [tenantSnapshot, tenantFilters]);

  const isCreateFormValid = useMemo(
    () => Object.keys(computeUniversityErrors(createForm)).length === 0,
    [createForm],
  );

  const isUniversityFormValid = useMemo(() => {
    if (!universityForm) {
      return false;
    }
    return Object.keys(computeUniversityErrors(universityForm)).length === 0;
  }, [universityForm]);

  const renderUniversitiesTab = () => (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Search tenants"
          value={tenantFilters.search}
          onChange={(event) => setTenantFilters((prev) => ({ ...prev, search: event.target.value }))}
          sx={{ maxWidth: 320 }}
        />
        <TextField
          select
          label="Status"
          value={tenantFilters.status}
          onChange={(event) => setTenantFilters((prev) => ({ ...prev, status: event.target.value }))}
          sx={{ maxWidth: 200 }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={openCreateUniversityDialog}
        >
          New university
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ p: 2 }}>
        {loadingTenants ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>University</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Credit balance</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Risk</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {filteredTenants.map((tenant) => {
                  const lastActivity = tenant.history?.lastActivity || tenant.activity?.lastHistoryAt;
                  return (
                  <TableRow
                    key={tenant.id}
                    hover
                    selected={selectedUniversityId === tenant.id}
                    onClick={() => handleSelectUniversity(tenant.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{tenant.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tenant.code}</Typography>
                    </TableCell>
                    <TableCell>{tenant.status}</TableCell>
                    <TableCell>
                      {tenant.aiFundingMode === AI_FUNDING_MODES.TENANT_BYOK
                        ? 'Not used (BYOK)'
                        : `${formatTokenBalance(tenant.walletBalance ?? tenant.wallet?.balance ?? 0)} credits`}
                    </TableCell>
                    <TableCell>{formatRelativeTime(lastActivity, 'No data')}</TableCell>
                    <TableCell><RiskChip riskLevel={tenant.riskLevel} /></TableCell>
                  </TableRow>
                );
                })}
              {!filteredTenants.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">No universities match your filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 3 }}>
        {loadingUniversityDetail ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !selectedUniversityId ? (
          <Typography variant="body2" color="text.secondary">
            Select a university from the table to view and edit its configuration.
          </Typography>
        ) : !universityDetail ? (
          <Typography variant="body2" color="text.secondary">
            Unable to load the selected university. Please try again.
          </Typography>
        ) : (
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h6">{universityDetail.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Code {universityDetail.code} | Status {universityDetail.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Users: {universityDetail.counts?.users ?? 0}
                </Typography>
              </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    onClick={saveUniversityChanges}
                    disabled={savingUniversity || !universityForm || !isUniversityFormValid}
                  >
                    {savingUniversity ? 'Saving...' : 'Save changes'}
                  </Button>
                <Tooltip title={universityDetail.status === 'archived' ? 'University already archived' : 'Archive university'}>
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<ArchiveIcon />}
                      onClick={archiveSelectedUniversity}
                      disabled={savingUniversity || universityDetail.status === 'archived'}
                    >
                      Archive
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Name"
                  value={universityForm?.name || ''}
                  onChange={handleUniversityFieldChange('name')}
                  error={Boolean(universityFormErrors.name)}
                  helperText={universityFormErrors.name}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Code"
                  value={universityForm?.code || ''}
                  onChange={handleUniversityFieldChange('code')}
                  error={Boolean(universityFormErrors.code)}
                  helperText={universityFormErrors.code}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Slug"
                  value={universityForm?.slug || ''}
                  onChange={handleUniversityFieldChange('slug')}
                  error={Boolean(universityFormErrors.slug)}
                  helperText={universityFormErrors.slug}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Status"
                  value={universityForm?.status || 'active'}
                  onChange={handleUniversityFieldChange('status')}
                  fullWidth
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Seat capacity"
                  type="number"
                  value={universityForm?.seatCapacity ?? 0}
                  onChange={handleUniversityFieldChange('seatCapacity')}
                  error={Boolean(universityFormErrors.seatCapacity)}
                  helperText={universityFormErrors.seatCapacity}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact email"
                  value={universityForm?.contactEmail || ''}
                  onChange={handleUniversityFieldChange('contactEmail')}
                  error={Boolean(universityFormErrors.contactEmail)}
                  helperText={universityFormErrors.contactEmail}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact phone"
                  value={universityForm?.contactPhone || ''}
                  onChange={handleUniversityFieldChange('contactPhone')}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Credit billing</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Credit price per history"
                      type="number"
                      value={universityForm?.creditUnitPrice || ''}
                      onChange={handleUniversityFieldChange('creditUnitPrice')}
                      helperText="Set the university-specific price (e.g., 0.40)"
                      inputProps={{ step: 0.01, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Currency"
                      value={universityForm?.creditCurrency || 'GBP'}
                      onChange={handleUniversityFieldChange('creditCurrency')}
                      fullWidth
                    >
                      <MenuItem value="GBP">GBP</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Allocation mode"
                      value={universityForm?.creditAllocationMode || 'assigned'}
                      onChange={handleUniversityFieldChange('creditAllocationMode')}
                      fullWidth
                    >
                      <MenuItem value="assigned">Assigned credits (per-student wallets)</MenuItem>
                      <MenuItem value="shared_pool">Shared pool (students draw from central balance)</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Administrators</Typography>
                {Array.isArray(universityDetail.administrators) && universityDetail.administrators.length ? (
                  <Stack spacing={1}>
                    {universityDetail.administrators.map((admin) => (
                      <Box
                        key={admin.id || admin.email}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', md: 'center' },
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">{[admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.email}</Typography>
                          <Typography variant="body2" color="text.secondary">{admin.email}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={admin.legacyRole || admin.role || 'admin'} />
                          <Typography variant="caption" color="text.secondary">
                            Last login: {admin.lastLoginAt ? `${formatDistanceToNowStrict(new Date(admin.lastLoginAt))} ago` : 'Never'}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No administrators assigned yet.</Typography>
                )}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                {assignAdminFeedback && (
                  <Alert
                    severity={mapFeedbackSeverity(assignAdminFeedback.type)}
                    onClose={() => setAssignAdminFeedback(null)}
                  >
                    {assignAdminFeedback.message}
                  </Alert>
                )}
                <Typography variant="subtitle1">Invite an administrator</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
                  <TextField
                    label="Admin email"
                    value={assignAdminForm.email}
                    onChange={handleAssignAdminFieldChange('email')}
                    type="email"
                    required
                    sx={{ flex: 1 }}
                    error={Boolean(assignAdminForm.email) && !assignAdminEmailValid}
                    helperText={assignAdminForm.email && !assignAdminEmailValid ? 'Enter a valid email address' : undefined}
                  />
                  <TextField
                    label="First name (optional)"
                    value={assignAdminForm.firstName}
                    onChange={handleAssignAdminFieldChange('firstName')}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Last name (optional)"
                    value={assignAdminForm.lastName}
                    onChange={handleAssignAdminFieldChange('lastName')}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={submitAssignAdmin}
                    disabled={assigningAdmin || !assignAdminEmailValid}
                  >
                    {assigningAdmin ? 'Sending...' : 'Send invite'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Pending admin invites</Typography>
                {Array.isArray(universityDetail.adminInvites) && universityDetail.adminInvites.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {universityDetail.adminInvites.map((invite) => (
                        <TableRow key={invite.id || invite.email}>
                          <TableCell>{invite.email}</TableCell>
                          <TableCell>{invite.status}</TableCell>
                          <TableCell>{formatInviteExpiry(invite.expiresAt)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleResendAdminInvite(invite.id)}
                                disabled={inviteActionState.resendId === invite.id}
                              >
                                {inviteActionState.resendId === invite.id ? 'Resending...' : 'Resend'}
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                onClick={() => handleRevokeAdminInvite(invite.id)}
                                disabled={inviteActionState.revokeId === invite.id}
                              >
                                {inviteActionState.revokeId === invite.id ? 'Revoking...' : 'Revoke'}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No pending admin invites.</Typography>
                )}
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <MetricCard
                title="Credit balance"
                value={`${formatTokenBalance(universityDetail.wallet?.balance ?? 0)} credits`}
                description={`Last top-up ${universityDetail.wallet?.lastTopUpAt ? formatDistanceToNowStrict(new Date(universityDetail.wallet.lastTopUpAt)) + ' ago' : 'N/A'}`}
                icon={<CheckCircleIcon color="success" />}
              />
              <MetricCard
                title="Recent history"
                value={universityDetail.activity?.lastHistoryAt
                  ? formatDistanceToNowStrict(new Date(universityDetail.activity.lastHistoryAt)) + ' ago'
                  : 'No submissions'}
                description="Most recent student submission"
                icon={<WarningAmberIcon color="warning" />}
              />
            </Stack>

            <UniversityAiConfigurationPanel
              alert={universityAiAlert}
              config={universityAiConfig}
              fundingMode={universityAiForm.funding?.mode}
              loading={universityAiLoading}
              saving={universityAiSaving}
              onAlertClose={() => setUniversityAiAlert(null)}
              onFundingModeChange={updateUniversityAiFundingMode}
              onReset={() => loadUniversityAiConfig(selectedUniversityId)}
              onSave={saveUniversityAiConfig}
            >
              <UniversityAiWorkloadSettings
                config={universityAiConfig}
                marking={markingTenantForm}
                normalizeReasoningEffort={normalizeGeminiReasoningEffort}
                onMarkingChange={updateUniversityMarkingField}
                onMarkingParameterChange={updateMarkingParameterField}
                onRealtimeChange={updateUniversityRealtimeField}
                onTranscriptionChange={updateUniversityTranscriptionField}
                realtime={realtimeTenantForm}
                reasoningEffortOptions={GEMINI_REASONING_EFFORT_OPTIONS}
                transcription={transcriptionTenantForm}
                verbosityDefault={defaultMarkingForm.parameters.verbosity}
                voices={universityAiConfig?.voices || settings?.openai?.voices || []}
              />
            </UniversityAiConfigurationPanel>
          </Stack>
        )}
      </Paper>
    </Stack>
  );

  const renderRealtimeTab = () => (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Realtime defaults for individual learners</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Provider"
              value={realtimeForm.provider}
              onChange={(event) => setRealtimeForm((prev) => ({
                ...prev,
                provider: event.target.value,
                parentApiKey: '',
              }))}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="azure">Azure OpenAI</MenuItem>
              <MenuItem value="gemini">Gemini Live</MenuItem>
            </TextField>
          </Stack>
        {realtimeForm.provider === 'openai' && (
          <TextField
            label="OpenAI API key"
            type="password"
            value={realtimeForm.parentApiKey}
            onChange={(event) => setRealtimeForm((prev) => ({ ...prev, parentApiKey: event.target.value }))}
            helperText={settings?.openai?.realtime?.openaiKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the API key used to mint realtime client secrets'}
          />
        )}
        {realtimeForm.provider === 'gemini' && (
          <Stack spacing={2}>
            <TextField
              label="Gemini API key"
              type="password"
              value={realtimeForm.parentApiKey}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, parentApiKey: event.target.value }))}
              helperText={settings?.openai?.realtime?.geminiKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the Gemini API key used to mint ephemeral auth tokens'}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtimeForm.proactiveAudio)}
                  onChange={(event) => setRealtimeForm((prev) => ({ ...prev, proactiveAudio: event.target.checked }))}
                />
              )}
              label="Enable proactive audio"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtimeForm.affectiveDialog)}
                  onChange={(event) => setRealtimeForm((prev) => ({ ...prev, affectiveDialog: event.target.checked }))}
                />
              )}
              label="Enable affective dialog"
            />
            <TextField
              select
              label="Reasoning effort"
              value={normalizeGeminiReasoningEffort(realtimeForm.reasoningEffort)}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, reasoningEffort: event.target.value }))}
              helperText="Controls Gemini Live thinking; off omits thinking configuration."
              sx={{ maxWidth: 260 }}
            >
              {GEMINI_REASONING_EFFORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Stack spacing={2}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(realtimeForm.contextWindowCompressionEnabled)}
                      onChange={(event) => setRealtimeForm((prev) => ({ ...prev, contextWindowCompressionEnabled: event.target.checked }))}
                    />
                  )}
                  label="Enable context window compression"
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Compression trigger tokens"
                    type="number"
                    value={realtimeForm.contextWindowCompressionTriggerTokens}
                    onChange={(event) => setRealtimeForm((prev) => ({ ...prev, contextWindowCompressionTriggerTokens: event.target.value }))}
                    disabled={!realtimeForm.contextWindowCompressionEnabled}
                    helperText="Blank uses Gemini default trigger."
                    sx={{ minWidth: 240 }}
                    inputProps={{ min: 1, step: 1 }}
                  />
                  <TextField
                    label="Compression target tokens"
                    type="number"
                    value={realtimeForm.contextWindowCompressionTargetTokens}
                    onChange={(event) => setRealtimeForm((prev) => ({ ...prev, contextWindowCompressionTargetTokens: event.target.value }))}
                    disabled={!realtimeForm.contextWindowCompressionEnabled}
                    helperText="Blank uses Gemini default target."
                    sx={{ minWidth: 240 }}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        )}
        {realtimeForm.provider === 'azure' && (
          <Stack spacing={2}>
            <Alert severity="info">
              Azure Realtime is WebRTC-only. The API key stays on this server and is used to mint a short-lived client secret for each Unity connection.
            </Alert>
            <TextField
              label="Azure endpoint"
              value={realtimeForm.azureEndpoint}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureEndpoint: event.target.value }))}
              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com)"
            />
            <TextField
              label="Azure OpenAI API key"
              type="password"
              value={realtimeForm.azureApiKey}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureApiKey: event.target.value }))}
              helperText={settings?.openai?.realtime?.azureKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the Azure OpenAI key for this deployment'}
            />
          </Stack>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Realtime model"
            value={realtimeForm.realtimeModel}
            onChange={(event) => setRealtimeForm((prev) => ({ ...prev, realtimeModel: event.target.value }))}
            sx={{ minWidth: 240 }}
            helperText={
              realtimeForm.provider === 'gemini'
                ? 'Enter the exact Gemini Live model id (for example: gemini-2.5-flash-native-audio-latest).'
                : (realtimeForm.provider === 'azure'
                  ? 'Enter the Azure Realtime deployment name.'
                  : (settings?.openai?.allowedRealtimeModels?.length
                  ? `Enter the exact OpenAI realtime model id (e.g., ${settings.openai.allowedRealtimeModels.slice(0, 3).join(', ')})`
                  : 'Enter the exact OpenAI realtime model id to use.'))
            }
            autoComplete="off"
            inputProps={{ spellCheck: 'false' }}
          />
          {realtimeForm.provider === 'gemini' ? (
            <TextField
              label="Voice preset"
              value={realtimeForm.voice}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, voice: event.target.value }))}
              sx={{ minWidth: 240 }}
              helperText="Gemini prebuilt voice name (for example: Kore, Puck, Charon)."
            />
          ) : (
            <TextField
              select
              label="Voice preset"
              value={realtimeForm.voice}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, voice: event.target.value }))}
              sx={{ minWidth: 240 }}
            >
              {(settings?.openai?.voices || []).map((voice) => (
                <MenuItem key={voice} value={voice}>{voice}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        <Divider />
        <Stack spacing={2}>
          <Typography variant="subtitle1">Input transcription override (optional)</Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(transcriptionForm.enabled)}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, enabled: event.target.checked }))}
              />
            )}
            label="Override user input transcription with backend gpt-4o-transcribe pipeline"
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Transcription provider"
              value={transcriptionForm.provider}
              onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, provider: event.target.value }))}
              sx={{ minWidth: 200 }}
              disabled={!transcriptionForm.enabled}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="azure">Azure OpenAI</MenuItem>
            </TextField>
            <TextField
              label="Transcription model"
              value={transcriptionForm.model}
              onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, model: event.target.value }))}
              sx={{ minWidth: 240 }}
              helperText="Recommended: gpt-4o-transcribe"
              disabled={!transcriptionForm.enabled}
            />
          </Stack>
          {transcriptionForm.provider === 'openai' && (
            <TextField
              label="OpenAI API key"
              type="password"
              value={transcriptionForm.parentApiKey}
              onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, parentApiKey: event.target.value }))}
              helperText={settings?.openai?.transcription?.openaiKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the API key used for transcription calls'}
              disabled={!transcriptionForm.enabled}
            />
          )}
          {transcriptionForm.provider === 'azure' && (
            <Stack spacing={2}>
              <TextField
                label="Azure endpoint"
                value={transcriptionForm.azureEndpoint}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureEndpoint: event.target.value }))}
                helperText="Base endpoint or full audio/transcriptions endpoint URL"
                disabled={!transcriptionForm.enabled}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Deployment id"
                  value={transcriptionForm.azureDeploymentId}
                  onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureDeploymentId: event.target.value }))}
                  helperText="Optional if full endpoint already contains deployment"
                  sx={{ minWidth: 200 }}
                  disabled={!transcriptionForm.enabled}
                />
                <TextField
                  label="API version"
                  value={transcriptionForm.azureApiVersion}
                  onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureApiVersion: event.target.value }))}
                  helperText="Required unless already included in endpoint"
                  sx={{ minWidth: 200 }}
                  disabled={!transcriptionForm.enabled}
                />
              </Stack>
              <TextField
                label="Resource name (optional)"
                value={transcriptionForm.azureResourceName}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureResourceName: event.target.value }))}
                disabled={!transcriptionForm.enabled}
              />
              <TextField
                label="Azure OpenAI API key"
                type="password"
                value={transcriptionForm.azureApiKey}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureApiKey: event.target.value }))}
                helperText={settings?.openai?.transcription?.azureKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the Azure API key for transcription'}
                disabled={!transcriptionForm.enabled}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(transcriptionForm.azureUseActiveDirectory)}
                    onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureUseActiveDirectory: event.target.checked }))}
                    disabled={!transcriptionForm.enabled}
                  />
                )}
                label="Use Azure Active Directory (access token authentication)"
              />
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary">
            This only affects user-input transcript text used in chat display and marking payloads. Realtime audio generation remains unchanged.
          </Typography>
        </Stack>

        {settings?.openai?.realtime?.lastUpdatedAt && (
          <Typography variant="body2" color="text.secondary">
            Last updated {formatDistanceToNowStrict(new Date(settings.openai.realtime.lastUpdatedAt))} ago
          </Typography>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => saveOpenAiSettings('realtime')}
            disabled={savingRealtime}
          >
            {savingRealtime ? 'Saving...' : 'Save realtime defaults'}
          </Button>
          <Button variant="outlined" onClick={loadSettings}>Reset</Button>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderActiveTab = () => {
    switch (TAB_KEYS[activeTab]) {
      case 'overview':
        return (
          <OverviewTab
            loadingSettings={loadingSettings}
            settings={settings}
            loadingTenants={loadingTenants}
            tenantSnapshot={tenantSnapshot}
          />
        );
      case 'universities':
        return renderUniversitiesTab();
      case 'case-transfer':
        return <CaseTransferManager />;
      case 'billing':
        return (
          <BillingTab
            pilotForm={pilotForm}
            onPilotFieldChange={handlePilotFieldChange}
            onSavePilotSettings={savePilotSettings}
            savingPilot={savingPilot}
            billingForm={billingForm}
            onBillingFieldChange={handleBillingFieldChange}
            onPackFieldChange={handlePackFieldChange}
            onAddTokenPack={addTokenPack}
            onRemoveTokenPack={removeTokenPack}
            onSaveBillingSettings={saveBillingSettings}
            savingBilling={savingBilling}
            onResetSettings={loadSettings}
            creditInvoiceFilters={creditInvoiceFilters}
            onCreditInvoiceFilterChange={handleCreditInvoiceFilterChange}
            onLoadCreditInvoices={loadCreditInvoices}
            creditInvoicesLoading={creditInvoicesLoading}
            creditInvoiceCreateForm={creditInvoiceCreateForm}
            onCreditInvoiceCreateFormChange={setCreditInvoiceCreateForm}
            tenantSnapshot={tenantSnapshot}
            onCreateCreditInvoice={handleCreateCreditInvoice}
            creatingCreditInvoice={creatingCreditInvoice}
            creditInvoiceAlert={creditInvoiceAlert}
            onDismissCreditInvoiceAlert={() => setCreditInvoiceAlert(null)}
            creditInvoices={creditInvoices}
            creditInvoiceActions={creditInvoiceActions}
            onDownloadCreditInvoicePdf={handleDownloadCreditInvoicePdf}
            onSendCreditInvoice={handleSendCreditInvoice}
            onMarkCreditInvoicePaid={handleMarkCreditInvoicePaid}
          />
        );
      case 'runtime-assets':
        return (
          <RuntimeAssetsTab
            addressablesByPlatform={addressablesByPlatform}
            addressablesForm={addressablesForm}
            addressablesUploadState={addressablesUploadState}
            addressableBuildsByPlatform={addressableBuildsByPlatform}
            addressablesOverview={addressablesOverview}
            addressablesBuildActionState={addressablesBuildActionState}
            addressablesEditState={addressablesEditState}
            onAddressablesFieldChange={handleAddressablesFieldChange}
            onUploadAddressables={handleUploadAddressables}
            onAddressablesBuildEditFieldChange={handleAddressablesBuildEditFieldChange}
            onSaveAddressablesBuild={handleSaveAddressablesBuild}
            onActivateAddressablesBuild={handleActivateAddressablesBuild}
            onDeleteAddressablesBuild={handleDeleteAddressablesBuild}
            voiceSampleProps={{
              voiceSampleForm,
              voiceOptionsForProvider,
              voiceSamplesOverview,
              selectedVoiceSample,
              voiceSampleActionState,
              onProviderChange: handleVoiceSampleProviderChange,
              onVoiceChange: handleVoiceSampleVoiceChange,
              onFileChange: handleVoiceSampleFileChange,
              onUpload: handleUploadVoiceSample,
              onDelete: handleDeleteVoiceSample,
              onRefresh: loadSettings,
              loading: loadingSettings,
            }}
          />
        );
      case 'realtime':
        return renderRealtimeTab();
      case 'marking':
        return (
          <MarkingTab
            form={markingForm}
            setForm={setMarkingForm}
            settings={settings}
            saving={savingMarking}
            onSave={() => saveOpenAiSettings('marking')}
            onReset={loadSettings}
          />
        );
      case 'compliance':
        return (
          <ComplianceTab
            settings={settings}
            financeOverview={financeOverview}
            loadingFinance={loadingFinance}
            exportingFinance={exportingFinance}
            onExportFinanceReport={handleExportFinanceReport}
          />
        );
      case 'features':
        return <FeatureChecklistTab items={featureChecklist} />;
      default:
        return null;
    }
  };

    return (
      <Box sx={{ p: 3 }} onKeyDown={handleTabKeyDown}>
        <Typography variant="h4" sx={{ mb: 2 }}>Global admin console</Typography>
        {(error || successMessage) && (
          <Box sx={{ mb: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            )}
            {successMessage && (
              <Alert severity="success" onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton
            aria-label="Previous tab"
            onClick={() => setActiveTab((prev) => Math.max(prev - 1, 0))}
            disabled={activeTab === 0}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Tabs
            value={activeTab}
            onChange={(_, index) => setActiveTab(index)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ flex: 1 }}
          >
            <Tab label="Overview" />
            <Tab label="Universities" />
            <Tab label="Case transfer" />
            <Tab label="Billing" />
            <Tab label="Runtime assets" />
            <Tab label="Realtime API" />
            <Tab label="Marking" />
            <Tab label="Compliance" />
            <Tab label="Feature checklist" />
          </Tabs>
          <IconButton
            aria-label="Next tab"
            onClick={() => setActiveTab((prev) => Math.min(prev + 1, TAB_KEYS.length - 1))}
            disabled={activeTab === TAB_KEYS.length - 1}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {renderActiveTab()}

        <Dialog open={createDialogOpen} onClose={closeCreateUniversityDialog} fullWidth maxWidth="sm">
          <DialogTitle>Create a new university</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                value={createForm.name}
                onChange={handleCreateFieldChange('name')}
                error={Boolean(createFormErrors.name)}
                helperText={createFormErrors.name || 'Enter the institution display name'}
                autoFocus
                fullWidth
              />
              <TextField
                label="Code"
                value={createForm.code}
                onChange={handleCreateFieldChange('code')}
                error={Boolean(createFormErrors.code)}
                helperText={createFormErrors.code || 'Short identifier used in reporting'}
                fullWidth
              />
              <TextField
                label="Slug"
                value={createForm.slug}
                onChange={handleCreateFieldChange('slug')}
                error={Boolean(createFormErrors.slug)}
                helperText={createFormErrors.slug || 'Optional: lower-case URL fragment (letters, numbers, hyphen)'}
                fullWidth
              />
              <TextField
                select
                label="Status"
                value={createForm.status}
                onChange={handleCreateFieldChange('status')}
                fullWidth
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
              <TextField
                label="Seat capacity"
                type="number"
                value={createForm.seatCapacity}
                onChange={handleCreateFieldChange('seatCapacity')}
                error={Boolean(createFormErrors.seatCapacity)}
                helperText={createFormErrors.seatCapacity || 'How many active learners are permitted'}
                inputProps={{ min: 0 }}
                fullWidth
              />
              <TextField
                label="Contact email"
                value={createForm.contactEmail}
                onChange={handleCreateFieldChange('contactEmail')}
                error={Boolean(createFormErrors.contactEmail)}
                helperText={createFormErrors.contactEmail || 'Primary contact for notifications'}
                fullWidth
              />
              <TextField
                label="Contact phone"
                value={createForm.contactPhone}
                onChange={handleCreateFieldChange('contactPhone')}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary">
                New universities are bootstrapped with default presenting complaints and specialties unique to their tenant.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreateUniversityDialog} disabled={creatingUniversity}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitCreateUniversity}
              disabled={creatingUniversity || !isCreateFormValid}
            >
              {creatingUniversity ? 'Creating...' : 'Create university'}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={toast.open}
          autoHideDuration={5000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleToastClose}
            severity={toast.severity}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };

export default GlobalAdminConsole;
